import { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Bot, User, Coins } from 'lucide-react';
import { Button } from './ui/button';

// FAQ data with Token system info
const FAQ_DATA = {
  // Dutch
  nl: {
    greeting: "Hoi! 👋 Ik ben Ella's assistent. Hoe kan ik je helpen?",
    questions: [
      { q: "Wat is Ella Gold?", a: "Ella Gold Official is een premium platform voor exclusieve AI-gegenereerde digitale kunst. Je krijgt toegang tot unieke kunstcollecties, custom creaties, en exclusieve content." },
      { q: "Wat zijn Ella Gold Tokens?", a: "Ella Gold Tokens zijn onze digitale valuta. Je koopt eenmalig tokens (vanaf €50) en besteedt ze in de shop. Tokens verlopen nooit! Bij registratie krijg je 5 gratis welkomst tokens." },
      { q: "Hoe koop ik tokens?", a: "Ga naar de Tokens pagina en kies een pakket: 50 tokens (€50), 100 tokens + 5 bonus (€95), of 200 tokens + 20 bonus (€180). Betaal via iDEAL of creditcard." },
      { q: "Hoe verdien ik gratis tokens?", a: "Je krijgt 5 tokens bij registratie! Daarnaast verdien je 10 tokens voor elke vriend die je uitnodigt via je referral link. Check je dashboard voor je unieke code!" },
      { q: "Kan ik met crypto betalen?", a: "Ja! We accepteren Bitcoin, Ethereum, USDT, USDC, Dogecoin en Litecoin. Klik op de ₿ knop naast elk product om met crypto te betalen." },
      { q: "Wat is de Art Builder?", a: "Met de Art Builder kun je je eigen custom artwork laten maken! Je kiest model, pose, outfit, mood en setting. Kosten: 3-10 tokens afhankelijk van het pakket." },
      { q: "Verlopen mijn tokens?", a: "Nee! Je Ella Gold Tokens verlopen nooit. Ze blijven voor altijd in je account en je kunt ze gebruiken wanneer je wilt." },
      { q: "Hoe word ik Gold member?", a: "Je wordt automatisch Gold member als je €30+ hebt uitgegeven. Gold members krijgen 10% korting en toegang tot de Discord VIP!" },
      { q: "Hoe kan ik contact opnemen?", a: "Je kunt ons bereiken via Instagram (@ella.gold.official), Telegram, of voor Gold members via onze Discord server." },
      { q: "Hoe werkt de shop?", a: "In de shop betaal je met Ella Gold Tokens. Elke prijs staat in tokens (bijv. 49 tokens voor de Prompt Guide). Geen tokens? Koop ze eerst op de Tokens pagina!" },
    ],
    fallback: "Ik begrijp je vraag niet helemaal. Probeer een van de onderstaande vragen of check de 'How It Works' pagina voor meer info!",
    placeholder: "Stel je vraag..."
  },
  // English
  en: {
    greeting: "Hi! 👋 I'm Ella's assistant. How can I help you?",
    questions: [
      { q: "What is Ella Gold?", a: "Ella Gold Official is a premium platform for exclusive AI-generated digital art. You get access to unique art collections, custom creations, and exclusive content." },
      { q: "What are Ella Gold Tokens?", a: "Ella Gold Tokens are our digital currency. You buy tokens once (from €50) and spend them in the shop. Tokens never expire! You get 5 free welcome tokens when you register." },
      { q: "How do I buy tokens?", a: "Go to the Tokens page and choose a package: 50 tokens (€50), 100 tokens + 5 bonus (€95), or 200 tokens + 20 bonus (€180). Pay via credit card or iDEAL." },
      { q: "How do I earn free tokens?", a: "You get 5 tokens when you register! Plus, earn 10 tokens for every friend you invite via your referral link. Check your dashboard for your unique code!" },
      { q: "Can I pay with crypto?", a: "Yes! We accept Bitcoin, Ethereum, USDT, USDC, Dogecoin and Litecoin. Click the ₿ button next to any product to pay with crypto." },
      { q: "What is the Art Builder?", a: "With the Art Builder you can create your own custom artwork! Choose model, pose, outfit, mood and setting. Costs: 3-10 tokens depending on the package." },
      { q: "Do my tokens expire?", a: "No! Your Ella Gold Tokens never expire. They stay in your account forever and you can use them whenever you want." },
      { q: "How do I become a Gold member?", a: "You automatically become Gold member when you've spent €30+. Gold members get 10% discount and Discord VIP access!" },
      { q: "How can I contact you?", a: "You can reach us via Instagram (@ella.gold.official), Telegram, or for Gold members via our Discord server." },
      { q: "How does the shop work?", a: "In the shop you pay with Ella Gold Tokens. Each price is in tokens (e.g. 49 tokens for the Prompt Guide). No tokens? Buy them first on the Tokens page!" },
    ],
    fallback: "I don't quite understand your question. Try one of the questions below or check the 'How It Works' page for more info!",
    placeholder: "Ask your question..."
  }
};

export default function ChatBot({ language = 'en' }) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const messagesEndRef = useRef(null);
  
  const lang = FAQ_DATA[language] || FAQ_DATA.en;

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{ type: 'bot', text: lang.greeting }]);
    }
  }, [isOpen, lang.greeting, messages.length]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const findAnswer = (question) => {
    const q = question.toLowerCase();
    
    // Token-related keywords
    if (q.includes('token') || q.includes('muntje') || q.includes('credit')) {
      if (q.includes('koop') || q.includes('buy') || q.includes('aanschaf')) {
        return lang.questions[2].a; // How to buy tokens
      }
      if (q.includes('verdien') || q.includes('gratis') || q.includes('free') || q.includes('earn')) {
        return lang.questions[3].a; // How to earn tokens
      }
      if (q.includes('verlop') || q.includes('expir')) {
        return lang.questions[6].a; // Do tokens expire
      }
      return lang.questions[1].a; // What are tokens
    }
    
    // Find best matching FAQ
    for (const faq of lang.questions) {
      const keywords = faq.q.toLowerCase().split(' ');
      const matches = keywords.filter(word => q.includes(word) && word.length > 3);
      if (matches.length >= 2 || q.includes(faq.q.toLowerCase().slice(0, 10))) {
        return faq.a;
      }
    }
    
    // Keyword matching
    if (q.includes('prijs') || q.includes('kost') || q.includes('price') || q.includes('cost')) {
      return lang.questions[2].a;
    }
    if (q.includes('crypto') || q.includes('bitcoin') || q.includes('btc')) {
      return lang.questions[4].a;
    }
    if (q.includes('builder') || q.includes('custom') || q.includes('maken')) {
      return lang.questions[5].a;
    }
    if (q.includes('shop') || q.includes('winkel') || q.includes('kopen')) {
      return lang.questions[9].a;
    }
    if (q.includes('gold') || q.includes('vip') || q.includes('member')) {
      return lang.questions[7].a;
    }
    if (q.includes('contact') || q.includes('help') || q.includes('support')) {
      return lang.questions[8].a;
    }
    if (q.includes('referral') || q.includes('friend') || q.includes('vriend') || q.includes('invite')) {
      return lang.questions[3].a;
    }
    
    return lang.fallback;
  };

  const handleSend = () => {
    if (!input.trim()) return;
    
    const userMessage = input.trim();
    setMessages(prev => [...prev, { type: 'user', text: userMessage }]);
    setInput('');
    
    // Simulate thinking
    setTimeout(() => {
      const answer = findAnswer(userMessage);
      setMessages(prev => [...prev, { type: 'bot', text: answer }]);
    }, 500);
  };

  const handleQuickQuestion = (question) => {
    setMessages(prev => [...prev, { type: 'user', text: question }]);
    
    setTimeout(() => {
      const faq = lang.questions.find(q => q.q === question);
      if (faq) {
        setMessages(prev => [...prev, { type: 'bot', text: faq.a }]);
      }
    }, 300);
  };

  return (
    <>
      {/* Chat Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 w-14 h-14 rounded-full bg-[#D4AF37] text-black flex items-center justify-center shadow-lg hover:bg-[#B8963A] transition-all z-50"
        aria-label="Open chat"
      >
        {isOpen ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 w-[350px] h-[500px] bg-[#0A0A0A] border border-white/10 rounded-2xl shadow-2xl flex flex-col z-50 overflow-hidden">
          {/* Header */}
          <div className="p-4 border-b border-white/10 bg-gradient-to-r from-[#D4AF37]/20 to-transparent">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#D4AF37] flex items-center justify-center">
                <Bot className="w-5 h-5 text-black" />
              </div>
              <div>
                <h3 className="font-semibold text-white">Ella's Assistant</h3>
                <p className="text-xs text-gray-400">Ask about tokens, shop & more</p>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`flex items-start gap-2 max-w-[85%] ${msg.type === 'user' ? 'flex-row-reverse' : ''}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    msg.type === 'user' ? 'bg-white/10' : 'bg-[#D4AF37]'
                  }`}>
                    {msg.type === 'user' ? (
                      <User className="w-4 h-4 text-white" />
                    ) : (
                      <Bot className="w-4 h-4 text-black" />
                    )}
                  </div>
                  <div className={`px-4 py-2 rounded-2xl ${
                    msg.type === 'user' 
                      ? 'bg-[#D4AF37] text-black' 
                      : 'bg-white/10 text-white'
                  }`}>
                    <p className="text-sm">{msg.text}</p>
                  </div>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Questions */}
          {messages.length <= 1 && (
            <div className="px-4 pb-2">
              <p className="text-xs text-gray-500 mb-2">Popular questions:</p>
              <div className="flex flex-wrap gap-2">
                {lang.questions.slice(0, 4).map((faq, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleQuickQuestion(faq.q)}
                    className="px-3 py-1 text-xs bg-white/5 hover:bg-white/10 rounded-full text-gray-300 transition-colors"
                  >
                    {faq.q.length > 25 ? faq.q.slice(0, 25) + '...' : faq.q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-4 border-t border-white/10">
            <div className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                placeholder={lang.placeholder}
                className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-full text-white placeholder:text-gray-500 focus:outline-none focus:border-[#D4AF37]"
              />
              <Button
                onClick={handleSend}
                className="w-10 h-10 rounded-full bg-[#D4AF37] hover:bg-[#B8963A] p-0"
              >
                <Send className="w-4 h-4 text-black" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
